import { useCallback, useEffect, useState } from "react";
import { isChatEmbeddedInHub } from "@/suiteSession";

export function isChatPwaStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return true;
  }
  const installedModes = [
    "standalone",
    "fullscreen",
    "minimal-ui",
    "window-controls-overlay",
  ] as const;
  return installedModes.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches);
}

export function isChatIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

type ChatPwaBridge = {
  isStandalone: boolean;
  isIos: boolean;
  canNativeInstall: boolean;
  showInstallUi: boolean;
  promptInstall: () => Promise<boolean>;
};

function readChatPwaBridge(): ChatPwaBridge {
  const standalone = isChatPwaStandalone();
  const embedded = isChatEmbeddedInHub();
  const bridge = (window as Window & { chatPwa?: ChatPwaBridge }).chatPwa;
  return {
    isStandalone: standalone,
    isIos: bridge?.isIos ?? isChatIos(),
    canNativeInstall: bridge?.canNativeInstall ?? false,
    showInstallUi: !standalone && !embedded,
    promptInstall: bridge?.promptInstall ?? (async () => false),
  };
}

export function useChatPwaInstall() {
  const [state, setState] = useState(readChatPwaBridge);

  useEffect(() => {
    const sync = () => setState(readChatPwaBridge());
    sync();
    window.addEventListener("chat-pwa-sync", sync);
    window.addEventListener("beforeinstallprompt", sync);
    window.addEventListener("appinstalled", sync);
    const onDisplayMode = () => sync();
    window.matchMedia("(display-mode: standalone)").addEventListener("change", onDisplayMode);
    window.matchMedia("(display-mode: window-controls-overlay)").addEventListener("change", onDisplayMode);
    return () => {
      window.removeEventListener("chat-pwa-sync", sync);
      window.removeEventListener("beforeinstallprompt", sync);
      window.removeEventListener("appinstalled", sync);
      window.matchMedia("(display-mode: standalone)").removeEventListener("change", onDisplayMode);
      window.matchMedia("(display-mode: window-controls-overlay)").removeEventListener("change", onDisplayMode);
    };
  }, []);

  const install = useCallback(async () => {
    return readChatPwaBridge().promptInstall();
  }, []);

  return {
    isStandalone: state.isStandalone,
    canNativeInstall: state.canNativeInstall,
    isIos: state.isIos,
    showInstallUi: state.showInstallUi,
    install,
  };
}

declare global {
  interface Window {
    chatPwa?: ChatPwaBridge;
    chatPwaApplyUpdate?: () => Promise<void>;
  }
}
